import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2Icon, MapPinIcon, XIcon } from 'lucide-react';
import { usePower } from '../../contexts/PowerContext';
import { POWER_STATE_LABEL, powerLocationById, powerLocations } from '../../data/power';
import { IconTile, SOURCE_ICON, powerIcons } from './PowerPrimitives';
import type { PowerSourceKind, PowerState } from '../../types/power';
const STATES: {
  state: PowerState;
  label: string;
  tone: string;
}[] = [{
  state: 'on',
  label: 'Power ON',
  tone: 'ok'
}, {
  state: 'off',
  label: 'Power OFF',
  tone: 'danger'
}, {
  state: 'unstable',
  label: 'Unstable',
  tone: 'warn'
}, {
  state: 'backup',
  label: 'Backup power',
  tone: 'info'
}];
const SOURCES: PowerSourceKind[] = ['grid', 'generator', 'solar', 'inverter', 'other', 'unknown'];
const SOURCE_LABEL: Record<PowerSourceKind, string> = {
  grid: 'Grid',
  generator: 'Generator',
  solar: 'Solar',
  inverter: 'Inverter',
  other: 'Other',
  unknown: "Don't know"
};

/**
 * Report power — three steps, because a student filing this is usually standing
 * up and in a hurry. Step 2 is genuinely optional and skippable, and the sheet
 * refuses a submission the limits reject rather than accepting it silently.
 */
export function ReportPowerSheet({
  open,
  onClose,
  presetLocationId
}: {
  open: boolean;
  onClose: () => void;
  presetLocationId?: string | null;
}) {
  const {
    submitReport,
    campusLocationId,
    offline
  } = usePower();
  const [step, setStep] = useState(1);
  const [state, setState] = useState<PowerState | null>(null);
  const [source, setSource] = useState<PowerSourceKind>('unknown');
  const [locationId, setLocationId] = useState<string>(presetLocationId ?? campusLocationId);
  const [choosing, setChoosing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setState(null);
    setSource('unknown');
    setLocationId(presetLocationId ?? campusLocationId);
    setChoosing(false);
    setError(null);
    setDone(false);
    setSubmitting(false);
  }, [campusLocationId, open, presetLocationId]);
  const location = useMemo(() => powerLocationById(locationId), [locationId]);
  if (!open) return null;
  const submit = () => {
    if (!state) return;
    setSubmitting(true);
    setError(null);
    // Kept deliberate rather than instant: the person should see it being sent.
    window.setTimeout(() => {
      const outcome = submitReport({
        locationId,
        state,
        source
      });
      setSubmitting(false);
      if (outcome.ok) setDone(true);else setError(outcome.reason);
    }, 450);
  };
  return <div className="fixed inset-0 z-[1100] flex items-end justify-center sm:items-center" data-id="element-4358" data-mp="2z720gn7gvvgo">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-ink/40" data-id="element-4359" data-mp="ga0q0ri95en" />
      <div role="dialog" aria-modal="true" aria-labelledby="report-power-title" className="relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-line bg-surface shadow-panel sm:max-w-lg sm:rounded-2xl" data-id="element-4360" data-mp="3rtmrtnny07k8">
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-line sm:hidden" aria-hidden data-id="element-4361" data-mp="cyw3kxle19f9" />
        <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-4" data-id="element-4362" data-mp="h0zki1qtt67x">
          <div data-id="element-4363" data-mp="m03qykmd8puk">
            <h2 id="report-power-title" className="text-h4 font-semibold text-ink" data-id="element-4364" data-mp="m0lwy8ytyifo">
              Report power
            </h2>
            {!done && <p className="text-caption text-ink-subtle" data-id="element-4365" data-mp="qx2kp224aus8">Step {step} of 3</p>}
          </div>
          <button type="button" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-lg text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-4366" data-mp="vq5i5la0modp">
            <XIcon className="h-5 w-5" aria-hidden data-id="element-4367" data-mp="14oh6thrbnc3i" />
            <span className="sr-only" data-id="element-4368" data-mp="19q6h2v6pzlqj">Close</span>
          </button>
        </div>

        {/* Progress, expressed as a bar and as text above it. */}
        {!done && <div className="px-5" data-id="element-4369" data-mp="s1m9mf90tkrh">
            <div className="h-1 w-full overflow-hidden rounded-full bg-canvas" data-id="element-4370" data-mp="s0kvu30mek62">
              <div className="h-full rounded-full bg-brand-700 transition-[width] duration-200 ease-out" style={{
            width: `${step / 3 * 100}%`
          }} data-id="element-4371" data-mp="15rfvcop8bowz" />
            </div>
          </div>}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4" data-id="element-4372" data-mp="d5whdzd8sbeb">
          {done ? <div className="py-4 text-center" data-id="element-4373" data-mp="941h8y71s424">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ok-50 text-ok-800" data-id="element-4374" data-mp="n2j12ktu0wer">
                <CheckCircle2Icon className="h-6 w-6" aria-hidden data-id="element-4375" data-mp="i1vc04p5ypn6" />
              </span>
              <p className="mt-3 text-h4 font-semibold text-ink" data-id="element-4376" data-mp="1ygn2gc2r1mtu">Report recorded</p>
              <p className="mx-auto mt-1 max-w-[38ch] text-body-sm text-ink-muted" data-id="element-4377" data-mp="1tibuk6w0lme9">
                {location?.name} is now carrying your report. It is combined with other
                recent reports rather than replacing them, so the status reflects
                agreement and not just the newest voice.
              </p>
            </div> : step === 1 ? <>
              <p className="text-body font-medium text-ink" data-id="element-4378" data-mp="37e3iew1hlcu2">
                What&apos;s the power situation here?
              </p>
              <ul className="mt-3 grid grid-cols-2 gap-2.5" data-id="element-4379" data-mp="3v4qqpifbe4h5">
                {STATES.map(option => <li key={option.state} data-id="element-4380" data-mp="2ytz1krqb4z5">
                    <button type="button" onClick={() => {
                setState(option.state);
                setStep(2);
              }} aria-pressed={state === option.state} className={`flex min-h-[6rem] w-full flex-col items-center justify-center gap-2 rounded-xl border px-3 py-3 transition-colors duration-150 ease-out ${state === option.state ? 'border-brand-700 bg-brand-50' : 'border-line bg-surface hover:border-line-strong'}`} data-id="element-4381" data-mp="31pv13k0njky">
                      <IconTile name={option.state === 'on' ? 'power' : option.state === 'off' ? 'off' : option.state === 'unstable' ? 'warn' : 'battery'} tone={option.tone as 'ok'} data-id="element-4382" data-mp="mk3xliphxvse" />
                      <span className="text-body-sm font-semibold text-ink" data-id="element-4383" data-mp="3v2t7r9on79s3">{option.label}</span>
                    </button>
                  </li>)}
              </ul>
              <p className="mt-3 text-caption text-ink-subtle" data-id="element-4384" data-mp="3m1v3qre7f29i">
                Your report is published as a community report. Your name is not attached to
                it.
              </p>
            </> : step === 2 ? <>
              <p className="text-body font-medium text-ink" data-id="element-4385" data-mp="25m7inhfz3tdg">
                What&apos;s powering this location?{' '}
                <span className="font-normal text-ink-subtle" data-id="element-4386" data-mp="2fiahoefj3bw6">(Optional)</span>
              </p>
              <ul className="mt-3 grid grid-cols-3 gap-2.5" data-id="element-4387" data-mp="1hv21myyaqv1k">
                {SOURCES.map(option => {
              const Icon = powerIcons[SOURCE_ICON[option]];
              return <li key={option} data-id="element-4388" data-mp="1rt83xho0m36y">
                      <button type="button" onClick={() => setSource(option)} aria-pressed={source === option} className={`flex min-h-[5.5rem] w-full flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-2.5 transition-colors duration-150 ease-out ${source === option ? 'border-brand-700 bg-brand-50' : 'border-line bg-surface hover:border-line-strong'}`} data-id="element-4389" data-mp="1rs6fmbhznk7a">
                        <Icon className={`h-5 w-5 ${source === option ? 'text-brand-700' : 'text-ink-muted'}`} aria-hidden data-id="element-4390" data-mp="6hiyw31rgreu" />
                        <span className="text-caption font-medium text-ink" data-id="element-4391" data-mp="bg2lm5o6i789">
                          {SOURCE_LABEL[option]}
                        </span>
                      </button>
                    </li>;
            })}
              </ul>
            </> : <>
              <p className="text-body font-medium text-ink" data-id="element-4392" data-mp="z4vneirhf9as">Where are you reporting from?</p>
              <ul className="mt-3 space-y-2.5" data-id="element-4393" data-mp="u6u5401vre0d">
                <li data-id="element-4394" data-mp="u857pwjlafpw">
                  <button type="button" onClick={() => setChoosing(false)} aria-pressed={!choosing} className={`flex min-h-touch w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors duration-150 ease-out ${!choosing ? 'border-brand-700 bg-brand-50' : 'border-line hover:border-line-strong'}`} data-id="element-4395" data-mp="u4hekyi23vbp">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700" data-id="element-4396" data-mp="v01kaevmgafo">
                      <MapPinIcon className="h-4 w-4" aria-hidden data-id="element-4397" data-mp="zufjythbh3q5" />
                    </span>
                    <span className="min-w-0" data-id="element-4398" data-mp="l59i0xr3gzek">
                      <span className="block text-body-sm font-medium text-ink" data-id="element-4399" data-mp="q1zxbiv2rqyx">
                        Use this location
                      </span>
                      <span className="block truncate text-caption text-ink-subtle" data-id="element-4400" data-mp="c4uyo3xoxudp">
                        Around {location?.name ?? 'your campus'}
                      </span>
                    </span>
                  </button>
                </li>
                <li data-id="element-4401" data-mp="1jk41o0gws8mv">
                  <button type="button" onClick={() => setChoosing(true)} aria-pressed={choosing} className={`flex min-h-touch w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors duration-150 ease-out ${choosing ? 'border-brand-700 bg-brand-50' : 'border-line hover:border-line-strong'}`} data-id="element-4402" data-mp="1jkn7wjx758l2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-canvas text-ink-muted" data-id="element-4403" data-mp="1ojgnchw1n9nv">
                      <powerIcons.building className="h-4 w-4" aria-hidden data-id="element-4404" data-mp="1sjh0lz7epetc" />
                    </span>
                    <span className="min-w-0" data-id="element-4405" data-mp="2g91wfions6k1">
                      <span className="block text-body-sm font-medium text-ink" data-id="element-4406" data-mp="2g89za3fey0rn">
                        Choose a specific location
                      </span>
                      <span className="block text-caption text-ink-subtle" data-id="element-4407" data-mp="9kje06bg7vgo">
                        Select from campus locations
                      </span>
                    </span>
                  </button>
                </li>
              </ul>

              {choosing && <label className="mt-3 block" data-id="element-4408" data-mp="ecax5vs9yyws">
                  <span className="text-caption font-medium text-ink-muted" data-id="element-4409" data-mp="eenm4swga9jc">Location</span>
                  <select value={locationId} onChange={event => setLocationId(event.target.value)} className="mt-1 min-h-touch w-full rounded-lg border border-line bg-surface px-3 text-body text-ink focus:border-brand-700 focus:outline-none" data-id="element-4410" data-mp="3msmfrnvy9ito">
                    {powerLocations.map(option => <option key={option.id} value={option.id} data-id="element-4411" data-mp="1byrs4udjd0fd">
                        {option.name}
                        {option.personal ? ' (your accommodation)' : ''}
                      </option>)}
                  </select>
                </label>}

              <p className="mt-3 text-caption text-ink-subtle" data-id="element-4412" data-mp="1j281b8xkdruh">
                Reports are attached to the location, never to your exact position.
              </p>
            </>}

          {error && <p role="alert" className="mt-3 rounded-lg border border-warn-100 bg-warn-50 px-3 py-2 text-body-sm text-warn-800" data-id="element-4413" data-mp="1t0xlj8cxnodw">
              {error}
            </p>}
          {offline && !done && <p className="mt-3 rounded-lg border border-warn-100 bg-warn-50 px-3 py-2 text-caption text-warn-800" data-id="element-4414" data-mp="15bl2lgrt01ye">
              You appear to be offline. Reports cannot be sent until the connection returns.
            </p>}
        </div>

        <div className="flex items-center gap-2.5 border-t border-line px-5 py-3" data-id="element-4415" data-mp="cn76j6k9zfph">
          {done ? <button type="button" onClick={onClose} className="min-h-touch flex-1 rounded-xl bg-brand-700 px-4 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800" data-id="element-4416" data-mp="hkfop75fbjqq">
              Done
            </button> : <>
              <button type="button" onClick={() => step === 1 ? onClose() : setStep(step - 1)} className="min-h-touch flex-1 rounded-xl border border-line bg-canvas px-4 text-body-sm font-medium text-ink transition-colors duration-150 ease-out hover:border-line-strong" data-id="element-4417" data-mp="2gxk3wl0n7dyo">
                {step === 1 ? 'Cancel' : step === 2 ? 'Skip' : 'Back'}
              </button>
              <button type="button" disabled={!state || submitting} onClick={() => step === 3 ? submit() : setStep(step + 1)} className="min-h-touch flex-1 rounded-xl bg-brand-700 px-4 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800 disabled:cursor-not-allowed disabled:bg-canvas disabled:text-ink-muted" data-id="element-4418" data-mp="1y7uaa18m8y8c">
                {step === 3 ? submitting ? 'Sending…' : 'Submit report' : 'Next'}
              </button>
            </>}
        </div>
      </div>
    </div>;
}
export { POWER_STATE_LABEL };